import {RcFile} from 'antd/es/upload';
import React, {FC} from 'react';
import {useDispatch} from 'react-redux';

import {IFieldMeta} from 'entities/metadata';
import {SignWithCryptoProModal} from 'features/sign-with-crypto-pro';
import {renameSignatureFile} from 'features/sign-with-crypto-pro/lib';
import {resetLoadedData} from 'modules/data';
import {EEntityType} from 'shared/constants/entities';
import {EFileExtensions} from 'shared/constants/file-extensions';
import {
    convertToFormData,
    showMessageFromResponse,
} from 'shared/utils';
import {TJsonFormValue} from 'shared/utils/convert-to-form-data';
import {useAppSelector} from 'store/config/hooks';
import {EDocumentsSigningMethod, selectDocumentsSigningMethod} from 'store/slices/documents-uploading-slice';

import {IColumnFormatterProps} from '../../table-types';
import {FileUploadCell} from '../file-upload-cell';
import {uploadAttachment} from './signature-reference-utils';

interface ISignatureReferenceProps extends IColumnFormatterProps {
    entityName: string;
    fieldMeta: IFieldMeta;
}

export const SignatureReference: FC<ISignatureReferenceProps> = ({
    value, record, entityName, fieldMeta,
}: ISignatureReferenceProps) => {
    const dispatch = useDispatch();

    const documentsSigningMethod = useAppSelector(selectDocumentsSigningMethod);

    const isEDSFileMethod = documentsSigningMethod === EDocumentsSigningMethod.UPLOAD_EDS_FILE;
    const isExternalServiceMethod = documentsSigningMethod === EDocumentsSigningMethod.EXTERNAL_SERVICE;
    const isCryptoProPluginMethod = documentsSigningMethod === EDocumentsSigningMethod.CRYPTO_PRO_PLUGIN;

    const handleUploadSignature = (signatureFile: File) => {
        if (signatureFile && fieldMeta?.referenceUrl) {
            uploadAttachment(fieldMeta.referenceUrl, convertToFormData({
                docId: record?.docId as TJsonFormValue,
                reportRow: record?.attachmentId as TJsonFormValue,
                reportRowId: record?.attachmentId as TJsonFormValue,
                attach: signatureFile as File,
            }))
                .then(data => {
                    if (data.status === 200) {
                        dispatch(resetLoadedData(entityName, EEntityType.TABLE));
                        showMessageFromResponse({response: data, isError: false});
                    }
                })
                .catch(error => {
                    showMessageFromResponse({response: error?.response, isError: true});
                });
        }
    };

    if ((isEDSFileMethod || isExternalServiceMethod || record?.signName) && record?.attachmentId) {
        return (
            <FileUploadCell
                value={value}
                fieldMeta={fieldMeta}
                record={record}
                entityName={entityName}
                isSignUpload
            />
        );
    }

    if (isCryptoProPluginMethod && record?.attachmentId) {
        const documentId = record?.attachmentId;
        return (
            <SignWithCryptoProModal
                attachmentIds={documentId ? [documentId as string] : []}
                handleSignedFileUpload={({signatureFile, selectedFileExtension}) => {
                    if (signatureFile) {
                        handleUploadSignature(
                            record?.fileName
                                ? renameSignatureFile(signatureFile, record?.fileName as string, selectedFileExtension!)
                                : signatureFile as RcFile,
                        );
                    }
                }}
                entityName={entityName}
                disabledFields={['signatureType', 'fileExtension', 'fileEncoding']}
                defaultFileExtension={EFileExtensions.BIN}
            />
        );
    }

    return null;
};
